import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiSave,
  FiCheckCircle,
  FiAlertTriangle,
  FiChevronRight,
  FiLayers,
  FiDollarSign,
  FiZap,
} from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { planosAPI, recursosAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.primary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? theme.colors.primary[500] : theme.colors.text.secondary};
`;

const StepIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $active }) =>
    $active ? theme.colors.primary[100] : theme.colors.background.primary};
  border: 2px solid
    ${({ $active }) => ($active ? theme.colors.primary[500] : theme.colors.border.primary)};
  color: ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.text.secondary)};
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
`;


const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Toggle = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.success[500] : theme.colors.border.primary)};
  background: ${({ $active }) => ($active ? theme.colors.success[100] : theme.colors.background.primary)};
  color: ${({ $active }) => ($active ? theme.colors.success[600] : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.success[600]};
    color: ${theme.colors.success[700]};
  }
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
`;

const StepsButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const ResumoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${theme.spacing[4]};
`;

const ResumoCard = styled.div`
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  background: ${theme.colors.background.primary};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ResumoTitle = styled.h4`
  margin: 0;
  font-size: ${theme.fontSizes.base};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const ResumoEntry = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.4;
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${theme.spacing[3]};
`;

const ResourceButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid
    ${({ $selected }) => ($selected ? theme.colors.primary[500] : theme.colors.border.primary)};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $selected }) =>
    $selected ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${({ $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.spacing[1]};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 8px 16px ${theme.colors.primary[500]}40` : 'none'};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${({ $selected }) =>
      $selected ? theme.colors.primary[600] : theme.colors.primary[50]};
    color: ${({ $selected }) =>
      $selected ? theme.colors.text.primary : theme.colors.primary[600]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ResourceButtonTitle = styled.span`
  font-weight: 700;
  font-size: ${theme.fontSizes.sm};
  word-break: break-word;
`;

const ResourceButtonDescription = styled.span`
  font-weight: 500;
  font-size: ${theme.fontSizes.xs};
  color: ${({ $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.text.muted};
  line-height: 1.4;
  word-break: break-word;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) =>
      $type === 'error'
        ? theme.colors.error[500]
        : $type === 'success'
        ? theme.colors.success[500]
        : theme.colors.warning[500]};
  background: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[100]
      : $type === 'success'
      ? theme.colors.success[100]
      : theme.colors.warning[100]};
  color: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[700]
      : $type === 'success'
      ? theme.colors.success[700]
      : theme.colors.warning[700]};
  font-size: ${theme.fontSizes.sm};
`;

const PlanoForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const planoId = params.id ? Number.parseInt(params.id, 10) : null;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    creditos_mensais: '',
    valor: '',
    ativo: true,
  });
  const [recursos, setRecursos] = useState([]);
  const [recursosSelecionados, setRecursosSelecionados] = useState([]);

  const tituloPagina = planoId ? 'Editar plano comercial' : 'Cadastrar novo plano';

  const carregarRecursos = async () => {
    try {
      const limit = 100;
      let page = 1;
      let continuar = true;
      const acumulados = [];

      while (continuar) {
        const response = await recursosAPI.listar({ limit, page });
        if (!response.data?.success) {
          throw new Error('Não foi possível carregar os recursos disponíveis.');
        }

        acumulados.push(...(response.data.data || []));

        const paginacao = response.data.pagination;
        if (paginacao?.hasNextPage) {
          page += 1;
        } else {
          continuar = false;
        }
      }

      setRecursos(acumulados);
    } catch (error) {
      console.error('Erro ao carregar recursos disponíveis', error);
      setRecursos([]);
    }
  };

  const carregarPlano = async () => {
    if (!planoId) {
      setLoading(false);
      return;
    }

    try {
      const response = await planosAPI.buscarPorId(planoId);
      if (response.data?.success) {
        const dados = response.data.data;
        setForm({
          nome: dados.nome || '',
          descricao: dados.descricao || '',
          creditos_mensais: dados.creditos_mensais || '',
          valor: dados.valor || '',
          ativo: !!dados.ativo,
        });
        setRecursosSelecionados((dados.recursos || []).map((recurso) => recurso.id));
      } else {
        setErro('Plano não encontrado.');
      }
    } catch (error) {
      console.error('Erro ao carregar plano', error);
      setErro('Erro ao carregar plano.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const iniciar = async () => {
      setLoading(true);
      await Promise.all([carregarRecursos(), carregarPlano()]);
      setLoading(false);
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoId]);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const toggleRecurso = (id) => {
    setRecursosSelecionados((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const podeAvancarStep1 = useMemo(() => {
    const creditos = Number.parseInt(form.creditos_mensais, 10);
    const valor = Number.parseFloat(form.valor);
    return (
      form.nome.trim().length > 0 &&
      Number.isFinite(creditos) &&
      creditos > 0 &&
      Number.isFinite(valor) &&
      valor >= 0
    );
  }, [form.creditos_mensais, form.nome, form.valor]);

  const podeFinalizar = useMemo(() => podeAvancarStep1, [podeAvancarStep1]);

  const handleNextStep = () => {
    if (step === 1 && !podeAvancarStep1) {
      setErro('Preencha os campos obrigatórios do passo 1 antes de avançar.');
      return;
    }

    setErro(null);
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePreviousStep = () => {
    setErro(null);
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const recursosSelecionadosDetalhes = useMemo(
    () => recursos.filter((recurso) => recursosSelecionados.includes(recurso.id)),
    [recursos, recursosSelecionados],
  );

  const handleSubmit = async () => {
    if (!podeFinalizar) {
      setErro('Preencha os campos obrigatórios antes de salvar.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      setSalvando(true);

      if (planoId) {
        const payload = {
          nome: form.nome.trim(),
          descricao: form.descricao || null,
          creditos_mensais: Number.parseInt(form.creditos_mensais, 10),
          valor: Number.parseFloat(form.valor),
          ativo: form.ativo,
        };

        const response = await planosAPI.atualizar(planoId, payload);
        if (!response.data?.success) {
          throw new Error('Não foi possível atualizar o plano.');
        }

        const syncResponse = await planosAPI.sincronizarRecursos(planoId, recursosSelecionados);
        if (!syncResponse.data?.success) {
          throw new Error('Plano atualizado, mas houve erro ao sincronizar recursos.');
        }

        setMensagem('Plano atualizado com sucesso.');
        setTimeout(() => {
          navigate('/gestao/planos');
        }, 1500);
      } else {
        const payload = {
          nome: form.nome.trim(),
          descricao: form.descricao || null,
          creditos_mensais: Number.parseInt(form.creditos_mensais, 10),
          valor: Number.parseFloat(form.valor),
          ativo: form.ativo,
          recurso_ids: recursosSelecionados,
        };

        const response = await planosAPI.criar(payload);
        if (!response.data?.success) {
          throw new Error('Não foi possível criar o plano.');
        }

        setMensagem('Plano criado com sucesso.');
        navigate('/gestao/planos');
        return;
      }
    } catch (error) {
      console.error('Erro ao salvar plano', error);
      setErro(error.message || 'Erro ao salvar plano.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <Layout title={tituloPagina}>
        <Container>
          <Card>
            <Subtitle>Carregando informações do plano...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={tituloPagina}>
      <Container>
        <Header>
          <HeaderGroup>
            <Title>{tituloPagina}</Title>
            <Subtitle>
              Defina a oferta comercial, créditos mensais e recursos disponíveis para clientes.
            </Subtitle>
          </HeaderGroup>
          <ActionBar>
            <GhostButton onClick={() => navigate('/gestao/planos')}>
              <FiArrowLeft />
              Voltar
            </GhostButton>
          </ActionBar>
        </Header>

        <Card>
          <Steps>
            <StepItem $active={step === 1}>
              <StepIndex $active={step === 1}>1</StepIndex>
              Dados base
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 2}>
              <StepIndex $active={step === 2}>2</StepIndex>
              Recursos e status
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 3}>
              <StepIndex $active={step === 3}>3</StepIndex>
              Revisão
            </StepItem>
          </Steps>

          {erro && (
            <Alert $type="error">
              <FiAlertTriangle />
              {erro}
            </Alert>
          )}

          {mensagem && (
            <Alert $type="success">
              <FiCheckCircle />
              {mensagem}
            </Alert>
          )}

          {step === 1 && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Nome do plano *</Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="Ex.: Plano Crescer"
                  />
                  <HelperText>Obrigatório. Máximo de 50 caracteres.</HelperText>
                </FormGroup>
                <FormGroup>
                  <Label>Créditos mensais *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.creditos_mensais}
                    onChange={(e) => handleChange('creditos_mensais', e.target.value)}
                    placeholder="Ex.: 100"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Valor mensal (R$) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.valor}
                    onChange={(e) => handleChange('valor', e.target.value)}
                    placeholder="Ex.: 499.90"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Status do plano</Label>
                  <Toggle $active={form.ativo} onClick={() => handleChange('ativo', !form.ativo)}>
                    {form.ativo ? 'Plano ativo' : 'Plano inativo'}
                  </Toggle>
                  <HelperText>
                    Planos inativos não ficarão disponíveis para novos contratos.
                  </HelperText>
                </FormGroup>
              </FormGrid>
              <FormGroup>
                <Label>Descrição</Label>
                <Textarea
                  value={form.descricao}
                  onChange={(e) => handleChange('descricao', e.target.value)}
                  placeholder="Detalhe o posicionamento comercial, público-alvo e observações."
                />
              </FormGroup>
            </>
          )}

          {step === 2 && (
            <>
              <FormGroup>
                <Label>Status do plano</Label>
                <Toggle $active={form.ativo} onClick={() => handleChange('ativo', !form.ativo)}>
                  {form.ativo ? 'Plano ativo' : 'Plano inativo'}
                </Toggle>
                <HelperText>Planos inativos não ficarão disponíveis para novos contratos.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>Recursos disponíveis no plano</Label>
                <HelperText>
                  Marque as funcionalidades que compõem este plano. Recomenda-se ao menos um recurso.
                </HelperText>
              </FormGroup>
              {recursos.length === 0 ? (
                <HelperText>
                  Nenhum recurso cadastrado. Cadastre recursos no menu “Recursos”.
                </HelperText>
              ) : (
                <ResourceGrid>
                  {recursos.map((recurso) => {
                    const selecionado = recursosSelecionados.includes(recurso.id);
                    return (
                      <ResourceButton
                        key={recurso.id}
                        type="button"
                        $selected={selecionado}
                        onClick={() => toggleRecurso(recurso.id)}
                      >
                        <ResourceButtonTitle>{recurso.codigo}</ResourceButtonTitle>
                        <ResourceButtonDescription $selected={selecionado}>
                          {recurso.descricao}
                        </ResourceButtonDescription>
                      </ResourceButton>
                    );
                  })}
                </ResourceGrid>
              )}
            </>
          )}

          {step === 3 && (
            <ResumoGrid>
              <ResumoCard>
                <ResumoTitle>Resumo geral</ResumoTitle>
                <ResumoEntry>
                  <FiLayers />
                  Nome: {form.nome || '—'}
                </ResumoEntry>
                <ResumoEntry>
                  <FiLayers />
                  Créditos mensais: {form.creditos_mensais || '—'}
                </ResumoEntry>
                <ResumoEntry>
                  <FiDollarSign />
                  Valor mensal: {form.valor ? `R$ ${Number(form.valor).toFixed(2)}` : '—'}
                </ResumoEntry>
                <ResumoEntry>
                  <FiZap />
                  Status: {form.ativo ? 'Plano ativo' : 'Plano inativo'}
                </ResumoEntry>
                <ResumoEntry>
                  Descrição: {form.descricao?.trim() ? form.descricao : '—'}
                </ResumoEntry>
              </ResumoCard>

              <ResumoCard>
                <ResumoTitle>Recursos selecionados</ResumoTitle>
                {recursosSelecionadosDetalhes.length > 0 ? (
                  recursosSelecionadosDetalhes.map((recurso) => (
                    <ResumoEntry key={recurso.id}>
                      <FiLayers />
                      {recurso.codigo} — {recurso.descricao}
                    </ResumoEntry>
                  ))
                ) : (
                  <HelperText>Nenhum recurso selecionado para este plano.</HelperText>
                )}
              </ResumoCard>
            </ResumoGrid>
          )}

          <NavigationBar>
            <StepsButtonGroup>
              {step > 1 && (
                <GhostButton type="button" onClick={handlePreviousStep}>
                  Voltar
                </GhostButton>
              )}

              {step < 3 && (
                <PrimaryButton
                  type="button"
                  onClick={handleNextStep}
                  disabled={step === 1 && !podeAvancarStep1}
                >
                  Próximo
                </PrimaryButton>
              )}
            </StepsButtonGroup>

            {step === 3 && (
              <PrimaryButton
                type="button"
                onClick={handleSubmit}
                disabled={salvando || !podeFinalizar}
              >
                <FiSave />
                {salvando ? 'Salvando...' : planoId ? 'Salvar alterações' : 'Criar plano'}
              </PrimaryButton>
            )}
          </NavigationBar>
        </Card>
      </Container>
    </Layout>
  );
};

export default PlanoForm;


